import React, { useState } from "react";
import { api, formatError } from "../../api";
import { X, Star, Loader2, CheckCircle2, XCircle, ChevronRight, Pause } from "lucide-react";

const DECISIONS = [
  { key: "strong_yes", label: "Strong yes", color: "#0F7C39" },
  { key: "yes",        label: "Yes",        color: "#16A34A" },
  { key: "maybe",      label: "Maybe",      color: "#CA8A04" },
  { key: "no",         label: "No",         color: "#DC2626" },
  { key: "strong_no",  label: "Strong no",  color: "#991B1B" },
];

const PROCEED = [
  { key: "next_round", label: "Next round",  icon: ChevronRight, color: "#9933FF" },
  { key: "offer",      label: "Send offer",  icon: CheckCircle2, color: "#16A34A" },
  { key: "hold",       label: "Put on hold", icon: Pause,        color: "#CA8A04" },
  { key: "reject",     label: "Reject",      icon: XCircle,      color: "#DC2626" },
];

/**
 * InterviewFeedbackModal — HR submits structured feedback for one interview round.
 *
 * Backend: POST /api/interviews/{interviewId}/complete
 *   body: { rating, decision, strengths, concerns, notes, proceed_to }
 * Calls onSubmitted(interview) on success.
 */
export default function InterviewFeedbackModal({
  interview,
  onClose,
  onSubmitted,
}) {
  const [rating, setRating] = useState(0);
  const [decision, setDecision] = useState("");
  const [strengths, setStrengths] = useState("");
  const [concerns, setConcerns] = useState("");
  const [proceedTo, setProceedTo] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!rating) { setErr("Please rate the candidate."); return; }
    if (!decision) { setErr("Please select a decision."); return; }
    setBusy(true); setErr("");
    try {
      const body = { rating, decision };
      if (strengths.trim()) body.strengths = strengths.trim();
      if (concerns.trim()) body.concerns = concerns.trim();
      if (notes.trim()) body.notes = notes.trim();
      if (proceedTo) body.proceed_to = proceedTo;
      const r = await api.post(`/interviews/${interview.id}/complete`, body);
      onSubmitted?.(r.data);
      onClose?.();
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="interview-feedback-modal">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <form onSubmit={submit} className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <header className="p-5 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--rule)" }}>
          <div>
            <div className="eyebrow inline-flex items-center gap-1.5"><Star size={11} /> Round {interview?.round_no || 1} feedback</div>
            <h2 className="font-display font-extrabold text-xl mt-1">{interview?.round_name || "Interview"} · {interview?.student_name || "candidate"}</h2>
            <p className="text-xs text-muted mt-1">Mark this round complete with structured feedback. The candidate sees only that the round is done.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-md hover:bg-paper grid place-items-center" data-testid="feedback-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2" data-testid="feedback-error">{err}</div>}

          <div>
            <label className="eyebrow">Overall rating</label>
            <div className="mt-1.5 flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  data-testid={`feedback-rating-${n}`}
                  className="w-10 h-10 grid place-items-center rounded-md transition hover:bg-paper"
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >
                  <Star
                    size={22}
                    className={n <= rating ? "fill-current" : ""}
                    style={{ color: n <= rating ? "#F59E0B" : "var(--rule)" }}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-muted font-mono">{rating || "—"}/5</span>
            </div>
          </div>

          <div>
            <label className="eyebrow">Decision</label>
            <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-5 gap-1.5">
              {DECISIONS.map((d) => {
                const active = decision === d.key;
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => setDecision(d.key)}
                    data-testid={`feedback-decision-${d.key}`}
                    className="px-2 py-2 rounded-lg border text-xs font-semibold transition"
                    style={{
                      borderColor: active ? d.color : "var(--rule)",
                      color: active ? "white" : d.color,
                      background: active ? d.color : "transparent",
                    }}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="eyebrow">Strengths</label>
            <textarea
              className="input mt-1.5 min-h-[80px]"
              value={strengths}
              onChange={(e) => setStrengths(e.target.value)}
              maxLength={2000}
              placeholder="What did the candidate do well? Communication, problem solving, depth…"
              data-testid="feedback-strengths"
            />
          </div>

          <div>
            <label className="eyebrow">Concerns / gaps</label>
            <textarea
              className="input mt-1.5 min-h-[80px]"
              value={concerns}
              onChange={(e) => setConcerns(e.target.value)}
              maxLength={2000}
              placeholder="What concerns came up? Where can they grow?"
              data-testid="feedback-concerns"
            />
          </div>

          <div>
            <label className="eyebrow">Next step</label>
            <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PROCEED.map((p) => {
                const Icon = p.icon;
                const active = proceedTo === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setProceedTo(active ? "" : p.key)}
                    data-testid={`feedback-proceed-${p.key}`}
                    className="px-3 py-2 rounded-lg border text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition"
                    style={{
                      borderColor: active ? p.color : "var(--rule)",
                      color: active ? "white" : p.color,
                      background: active ? p.color : "transparent",
                    }}
                  >
                    <Icon size={13} /> {p.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted mt-1.5">Pick one to update the candidate&apos;s application status automatically.</p>
          </div>

          <div>
            <label className="eyebrow">Private notes (optional)</label>
            <textarea
              className="input mt-1.5 min-h-[60px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              placeholder="Anything else the team should know…"
              data-testid="feedback-notes"
            />
          </div>
        </div>

        <footer className="p-5 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--rule)" }}>
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="feedback-cancel">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="feedback-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {busy ? "Submitting…" : "Submit feedback"}
          </button>
        </footer>
      </form>
    </div>
  );
}
